
import { supabase } from '@/integrations/supabase/client';
import { CategoryType } from '@/constants/categoryTypes';

export interface CategoryWithDetails {
  id: string;
  name: string;
  status: string;
  category_type: CategoryType;
}

// Get all unique category names
export const getCategories = async () => {
  const { data, error } = await supabase
    .from('categories')
    .select('name');
  
  if (error) throw error;
  return data.map(row => row.name);
};

// Get all categories with their details
export const getCategoriesWithDetails = async (): Promise<CategoryWithDetails[]> => {
  const { data, error } = await supabase
    .from('categories')
    .select('id, name, status, category_type');
  
  if (error) throw error;
  return data as CategoryWithDetails[];
};

// Create a new category
export const createCategory = async (categoryName: string, categoryType: CategoryType = 'Other') => {
  const { data, error } = await supabase
    .from('categories')
    .insert({ name: categoryName, category_type: categoryType })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Update an existing category
export const updateCategory = async (params: { 
  oldName: string; 
  newName: string;
  status?: string;
  category_type?: CategoryType;
}) => {
  // First get the category ID
  const { data: categoryData, error: fetchError } = await supabase
    .from('categories')
    .select('id')
    .eq('name', params.oldName)
    .single();
  
  if (fetchError) throw fetchError;
  
  // Update the category
  const updateData: { name: string; status: string; category_type?: CategoryType } = { 
    name: params.newName,
    status: params.status || 'active'
  };
  
  if (params.category_type) {
    updateData.category_type = params.category_type;
  }
  
  const { data, error } = await supabase
    .from('categories')
    .update(updateData)
    .eq('id', categoryData.id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Delete a category
export const deleteCategory = async (categoryName: string) => {
  const { error } = await supabase
    .from('categories')
    .delete()
    .eq('name', categoryName);
  
  if (error) throw error;
  return true;
};

// Bulk create categories
export const bulkCreateCategories = async (categoryNames: string[], categoryType: CategoryType = 'Other') => {
  try {
    // First, get existing categories to check for duplicates
    const { data: existingCategories, error: fetchError } = await supabase
      .from('categories')
      .select('name');
    
    if (fetchError) throw fetchError;
    
    // Filter out category names that already exist
    const existingNames = existingCategories ? new Set(existingCategories.map(cat => cat.name.toLowerCase())) : new Set();
    const duplicateNames = categoryNames.filter(name => existingNames.has(name.toLowerCase()));
    
    // Get unique new categories to add (case insensitive)
    const uniqueNewCategories = categoryNames.filter(name => !existingNames.has(name.toLowerCase()));
    const uniqueCategorySet = new Set(uniqueNewCategories.map(name => name.toLowerCase()));
    
    // Also check for duplicates within the upload list itself
    const internalDuplicates = uniqueNewCategories.filter(name => {
      const lowerName = name.toLowerCase();
      if (uniqueCategorySet.has(lowerName)) {
        uniqueCategorySet.delete(lowerName); // Remove so we only count subsequent occurrences
        return false;
      }
      return true;
    });
    
    // If no new categories to add, return early
    if (uniqueNewCategories.length === 0) {
      return { 
        added: 0,
        skipped: duplicateNames.length,
        duplicates: duplicateNames
      };
    }
    
    // Prepare categories for insertion
    const categoriesToInsert = uniqueNewCategories.map(name => ({ 
      name, 
      category_type: categoryType 
    }));
    
    // Insert new categories
    const { error } = await supabase
      .from('categories')
      .insert(categoriesToInsert);
    
    if (error) throw error;
    
    return { 
      added: uniqueNewCategories.length, 
      skipped: duplicateNames.length,
      duplicates: duplicateNames
    };
  } catch (error) {
    console.error('Error in bulk create categories:', error);
    throw error;
  }
};
